import { isString } from "@common/helpers/typeGuards";
import { useCallback, useEffect, useRef, useState } from "react";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";

export function usePayPalAuthorization(
    authorizationHandler: (authCode: string, sharedId: string) => void,
) {
    const paypalWindowRef = useRef<Window | null | undefined>(undefined);
    const { refuseIfViewOnly } = useViewOnlyRefusal();
    const [handlerActive, setHandlerActive] = useState(false);
    const initializeConnection = useCallback(() => {
        // Opens PayPal's onboarding in a new window, so the server never sees
        // this step.
        if (refuseIfViewOnly()) {
            return;
        }

        paypalWindowRef.current = window.open("/api/paypal-auth");
        setHandlerActive(true);
    }, [refuseIfViewOnly]);

    useEffect(() => {
        if (!handlerActive) {
            return;
        }

        const eventHandler = (event: MessageEvent) => {
            if (!isString(event.data)) {
                return;
            }
            try {
                const data = JSON.parse(event.data);

                if (!data.onboardedCompleteToken || !data.sharedId) {
                    return;
                }

                paypalWindowRef.current?.close();
                setHandlerActive(false);
                authorizationHandler(
                    data.onboardedCompleteToken,
                    data.sharedId,
                );
            } catch (error) {
                if (error instanceof SyntaxError) {
                    return;
                }
                throw error;
            }
        };

        window.addEventListener("message", eventHandler);

        return () => {
            window.removeEventListener("message", eventHandler);
        };
    }, [authorizationHandler, handlerActive]);

    return {
        initializeConnection,
    };
}
